import { MessageSquarePlus } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useMainScrollEdge } from "@/hooks/useMainScrollEdge";
import { useKaiChat } from "./KaiChatContext";
import { KaiConversationList } from "./KaiConversationList";
import { kaiHistorySidebarWidthClass } from "./kaiPageLayout";
import { RailFadeScroller } from "./KaiSidePanel";

/** Desktop Kai page — history column between the icon rail and main chat. */
export function KaiHistorySidebar({ className }: { className?: string }) {
  const kai = useKaiChat();
  const scrollRef = useMainScrollEdge<HTMLDivElement>("kai-page-history");
  if (!kai) return null;

  const {
    conversations,
    activeId,
    selectConversation,
    startNewChat,
  } = kai;

  return (
    <aside
      aria-label="Chat history"
      className={cn(
        // Reserve the floating top-bar band so the card (and its New chat
        // button) never sit under the centered top-bar pills — which otherwise
        // swallow the clicks.
        "relative flex min-h-0 shrink-0 flex-col pt-[var(--main-scroll-top)]",
        kaiHistorySidebarWidthClass,
        className,
      )}
    >
      <div className="glass-panel relative isolate flex min-h-0 flex-1 flex-col overflow-hidden p-2">
        <div className="relative z-10 flex shrink-0 flex-col gap-2 px-1 pb-2">
          <h2 className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
            Chats
          </h2>
          <Button
            type="button"
            variant="clay"
            onClick={startNewChat}
            className="h-9 w-full justify-start gap-2 text-[13px]"
          >
            <MessageSquarePlus className="size-4" />
            New chat
          </Button>
        </div>
        <RailFadeScroller
          scrollRef={scrollRef}
          underRailHeader={false}
          contentMask={false}
          className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1 pb-[var(--scroll-edge-bottom-fade-height)]"
        >
          <KaiConversationList
            conversations={conversations}
            activeId={activeId}
            onSelect={selectConversation}
            onNewChat={startNewChat}
            variant="glass"
            scrollMode="external"
            showNewChat={false}
            className="min-h-0"
            listClassName="overflow-visible"
          />
        </RailFadeScroller>
      </div>
    </aside>
  );
}
